import React, { FunctionComponent } from 'react'
import { Text as RNText, StyleSheet, View, ViewProps } from 'react-native'

import { capitalize } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { BADGE_HEIGHT, BadgeShape, BadgeVariant, borderRadius, padding } from '../../badge/constants'

const StyledText = styled(RNText)<{ bold: boolean; color: string }>(({ bold, color }) => ({
  fontSize: 12,
  lineHeight: 16,
  fontWeight: bold ? '600' : '500',
  color,
  textAlign: 'center',
}))

export interface TextBadgeProps extends ViewProps {
  /** Badge label */
  text: string | React.ReactNode
  /** Design‑system status */
  variant?: BadgeVariant
  /** `round` or `rect` */
  shape?: BadgeShape
  /** Text weight (defaults to medium) */
  bold?: boolean
  /** Skip auto sentence‑case */
  preserveCase?: boolean
  /** e2e selector */
  testID?: string
  /** Background color override */
  backgroundColor?: string
  /** Auto width (default: false for round, true for rect) */
  autoWidth?: boolean
}

/**
 * Standard badge for displaying text in a styled container
 */
export const TextBadge: FunctionComponent<TextBadgeProps> = ({
  text,
  variant = BadgeVariant.NEUTRAL,
  shape = BadgeShape.ROUND,
  bold = false,
  preserveCase = false,
  backgroundColor,
  testID = 'TextBadge',
  style,
  autoWidth = false,
  ...rest
}) => {
  const { colors } = useTheme()

  const getBadgeColors = () => {
    const badgeTokens = (colors as any).components?.badge
    if (!badgeTokens) {
      return {
        bg: colors.backgroundError || '#FF0000',
        fg: colors.textInverse || '#FFFFFF',
      }
    }

    switch (variant) {
      case BadgeVariant.SUCCESS:
        return { bg: badgeTokens.success.background, fg: badgeTokens.success.text }
      case BadgeVariant.ATTENTION:
        return { bg: badgeTokens.attention.background, fg: badgeTokens.attention.text }
      case BadgeVariant.ALERT:
        return { bg: badgeTokens.alert.background, fg: badgeTokens.alert.text }
      case BadgeVariant.NEUTRAL:
        return { bg: badgeTokens.neutral.background, fg: badgeTokens.neutral.text }
      case BadgeVariant.INFO_PRIMARY:
        return { bg: badgeTokens.infoPrimary.background, fg: badgeTokens.infoPrimary.text }
      case BadgeVariant.INFO_SECONDARY:
        return { bg: badgeTokens.infoSecondary.background, fg: badgeTokens.infoSecondary.text }
      case BadgeVariant.INVERSE:
        return { bg: badgeTokens.inverse.background, fg: badgeTokens.inverse.text }
      case BadgeVariant.NEUTRAL_TRANSPARENT:
        return { bg: badgeTokens.neutralTransparent.background, fg: badgeTokens.neutralTransparent.text }
      default:
        return { bg: badgeTokens.neutral.background, fg: badgeTokens.neutral.text }
    }
  }

  const badgeColors = getBadgeColors()
  const bg = backgroundColor ?? badgeColors.bg
  const fg = badgeColors.fg

  const textString = typeof text === 'string' ? text : '' // guarantee string for processing

  // Apply case conversion based on shape
  let renderedText: string
  if (shape === BadgeShape.RECT) {
    // RECT shape: always uppercase, ignoring preserveCase
    renderedText = textString.toUpperCase()
  } else {
    // ROUND shape: sentence case unless preserveCase is true
    renderedText = preserveCase ? textString : capitalize(textString)
  }

  const finalText = typeof text === 'string' ? renderedText : text // use original if React node

  return (
    <View
      testID={testID}
      style={[
        styles.container,
        padding[shape].text,
        {
          backgroundColor: bg,
          borderRadius: borderRadius[shape],
          height: BADGE_HEIGHT,
          width: autoWidth ? 'auto' : undefined,
          alignSelf: 'center',
        },
        style,
      ]}
      {...rest}
    >
      {!!finalText &&
        (typeof finalText === 'string' ? (
          <StyledText testID={`TID-${testID}`} bold={bold} color={fg}>
            {finalText}
          </StyledText>
        ) : (
          finalText
        ))}
    </View>
  )
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
})
